import { patchState, signalState } from '@ngrx/signals';
import { effect, inject, Injectable, signal } from "@angular/core";
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { catchError, exhaustMap, pipe, tap, throwError } from 'rxjs';
import { QuiztimeApiService } from '../services/quiztime-api.service';
import { User } from '../models/user.interface';
import { Token, TokenStorageService } from '../services/token-storage/token-storage.service';
import { LOCAL_STORAGE_REDIRECT_URL, REDIRECT_FOR_LOGIN_URL, REFRESH_TOKEN_FROM_CATALOG_INTERVAL } from '../constants';
import { LoggerService } from '../services/logger.service';

type AuthState = {
  user: User | null,
  is_loading: boolean,
}

const initialState: AuthState = {
  user: null,
  is_loading: false,
}

@Injectable({
  providedIn: 'root'
})
export class AuthStore {
  readonly #apiService = inject(QuiztimeApiService);
  readonly #tokenService = inject(TokenStorageService);
  private logger = inject(LoggerService);
  readonly #state = signalState(initialState);
  readonly user = this.#state.user;
  readonly isLoading = this.#state.is_loading;
  refreshFn = signal<any>(null);

  readonly updateUser = (user: User) => {
    patchState(this.#state, { user });
    this.ensureRefreshRunning();
  }

  updateUser$ = this.#apiService.getUserInfo()
    .pipe(
      tap(() => patchState(this.#state, { is_loading: true })),
      exhaustMap(() => {
        return this.#apiService.getUserInfo().pipe(
          tapResponse({
            next: (user: User) => this.updateUser(user),
            error: () => patchState(this.#state, { user: null }),
            complete: () => patchState(this.#state, { is_loading: false }),
          })
        )
      })
  );

  readonly updateUserRx = rxMethod<void>(
    pipe(
      tap(() => patchState(this.#state, { is_loading: true })),
      exhaustMap(() => {
        return this.#apiService.getUserInfo().pipe(
          tapResponse({
            next: (user: User) => this.updateUser(user),
            error: () => {
              patchState(this.#state, { user: null });
              this.cancelRefresh();
            },
            complete: () => patchState(this.#state, { is_loading: false }),
          })
        )
      })
    )
  )

  constructor() {
    if (localStorage.getItem('user') !== 'undefined' && !this.user()) {
      this.updateUser(JSON.parse(localStorage.getItem('user')!));
    }
    effect(() => {
      localStorage.setItem('user', JSON.stringify(this.user()));
    });
  }

  ensureRefreshRunning() {
    if (!this.refreshFn()) {
      this.setupRefreshTimer();
    }
  }

  setupRefreshTimer() {
    const token = this.#tokenService.getToken();
    const expirationTime = token?.expiration_timestamp;
    let refreshInterval = REFRESH_TOKEN_FROM_CATALOG_INTERVAL;

    if (expirationTime === undefined && token?.expires_in !== undefined) {
      // expires_in is in seconds, subtract 5 minutes (300s) for early refresh
      const expiresIn = token.expires_in;
      let intervalSeconds = expiresIn - 300;
      if (intervalSeconds <= 0) {
        this.refreshToken();
        return;
      }
      refreshInterval = intervalSeconds * 1000;
      this.logger.log('Setting token refresh timer using expires_in for', refreshInterval, 'ms from now');
    }
    else if (expirationTime) {
      const currentTime = Math.floor(Date.now() / 1000);
      const timeToExpiration = expirationTime - currentTime;

      if (timeToExpiration <= 300) {
        this.refreshToken();
        return;
      }

      refreshInterval = (timeToExpiration - 300) * 1000;
      this.logger.log('Setting token refresh timer for', refreshInterval, 'ms from now');
    }
    else {
      this.logger.log('No expiration timestamp or expires_in found, using default refresh interval');
    }

    const refresh = setTimeout(() => {
      this.refreshToken();
      this.cancelRefresh();
      this.setupRefreshTimer();
    }, refreshInterval);

    this.refreshFn.set(refresh);
  }

  refreshToken() {
    this.logger.log('Refreshing token for user', this.user());
    if (this.user()?.ltiUser.email !== undefined) {
      this.#apiService
        .getRefreshToken(this.user()?.ltiUser?.email as string)
        .pipe(
          catchError(err => this.handleRefreshError(err)),
          tap((resp: Token) => this.#tokenService.setToken(resp)),
        )
        .subscribe(() => this.setupRefreshTimer());
    }
  }

  handleRefreshError = (err: any) => {
    this.logger.error('Error refreshing token', err);
    this.#tokenService.clear();
    localStorage.setItem(LOCAL_STORAGE_REDIRECT_URL, window.location.pathname);
    this.cancelRefresh();
    window.location.href = REDIRECT_FOR_LOGIN_URL;
    return throwError(() => new Error('Could not refresh token, redirecting to login.'));
  }

  cancelRefresh() {
    if (this.refreshFn()) {
      clearTimeout(this.refreshFn());
      this.refreshFn.set(null);
    }
  }
}
